import { dayOf, hourOf, monthOf, weekdayOf } from './format.js';
import { CFG, UNALLOCATED_LABEL, deliveryOf, supplierLabel, supplierNames } from './model.js';
import { cardPaymentsOf } from './cards.js';
import { utcToLocal } from './time.js';

/* --------------------------------------------------------------------------
   Inläsning och normalisering
   -------------------------------------------------------------------------- */

/** Läser .json eller .zip och returnerar parsat objekt. */
export async function readFile(file){
  if(file.size > CFG.MAX_FILE_MB * 1024 * 1024){
    throw new AppError('Filen är för stor',
      `Filen är ${Math.round(file.size/1048576)} MB. Dela upp exporten i kortare perioder och försök igen.`);
  }
  const name = file.name.toLowerCase();

  if(name.endsWith('.zip')){
    if(typeof JSZip === 'undefined'){
      throw new AppError('Kunde inte packa upp filen',
        'Ladda om sidan och försök igen. Går det fortfarande inte, packa upp zip-filen och ladda upp .json-filen i den.');
    }
    let zip;
    try{
      zip = await JSZip.loadAsync(file);
    }catch{
      throw new AppError('Zip-filen gick inte att öppna',
        'Filen verkar vara skadad. Gör en ny kvittoexport från Back office.');
    }
    const entries = Object.values(zip.files)
      .filter(f => !f.dir && f.name.toLowerCase().endsWith('.json') && !f.name.startsWith('__MACOSX'));
    if(!entries.length){
      throw new AppError('Zip-filen innehåller ingen kvittoexport',
        'Zip-filen innehåller ingen .json-fil. Kontrollera att det är en kvittoexport från Back office.');
    }
    // Störst fil = kvittodatan, om zip:en råkar innehålla flera
    entries.sort((a,b) => (b._data?.uncompressedSize||0) - (a._data?.uncompressedSize||0));
    return parseJSON(await entries[0].async('string'));
  }

  if(name.endsWith('.json')){
    return parseJSON(await file.text());
  }

  throw new AppError('Filformatet stöds inte',
    'Ladda upp kvittoexporten som .zip eller .json.');
}

export function parseJSON(text){
  try{
    return JSON.parse(text.replace(/^\uFEFF/,''));
  }catch{
    throw new AppError('Filen gick inte att läsa',
      'Innehållet är inte giltig JSON. Gör en ny kvittoexport från Back office.');
  }
}

export class AppError extends Error{
  constructor(title, detail){ super(title); this.title = title; this.detail = detail; }
}

/**
 * Normaliserar råa kvitton till ett format som är billigt att filtrera och räkna på.
 * All klassificering av rader sker här, en gång, inte i varje beräkning.
 */
export function normalize(raw){
  if(!raw || !Array.isArray(raw.items)){
    throw new AppError('Filen innehåller inga kvitton',
      'Kontrollera att filen är en kvittoexport från Back office.');
  }

  const out = [];

  for(const r of raw.items){
    // Exporten har UTC. ts är svensk tid från och med här, se calc/time.js.
    const ts = utcToLocal(r.dateCreated);
    if(!ts) continue;   // utan tid går kvittot inte att placera

    const rec = {
      ts,
      day: dayOf(ts),
      month: monthOf(ts),
      hour: hourOf(ts),
      weekday: weekdayOf(ts),
      store: r.storeNumber ?? null,
      // Butikens namn och gatuadress på kvittot, för etiketten i storeLabels()
      storeName: typeof r.companyName === 'string' ? r.companyName : '',
      storeAddress: typeof r.address1 === 'string' ? r.address1 : '',
      register: r.cashRegisterNumber ?? null,
      cashier: r.cashierUsername || 'Okänd',
      number: r.receiptNumber ?? null,
      delivery: deliveryOf(r),
      copies: Math.max(0, Number(r.numberOfCopiesPrinted) || 0),
      // Kunden i lojalitetsprogrammet. Fälten saknas helt på kvitton utan kund.
      customer: r.customerNumber != null ? String(r.customerNumber).trim() : '',
      customerName: typeof r.customerName === 'string' ? r.customerName.trim() : '',
      hasCustomer: false,
      roundOff: Number(r.roundOff) || 0,
      paid: 0,
      payments: [],
      cards: cardPaymentsOf(r),   // kortbetalningar med korttyp, se calc/cards.js
      lines: [],          // säljbara rader: varor och tjänster
      revenue: 0,         // inkl moms
      revenueEx: 0,       // exkl moms
      profit: 0,
      units: 0,
      giftCardBooked: 0,  // presentkort, belopp som faktiskt bokfördes på kvittot
      giftCardFace: 0,    // presentkort, nominellt värde
      giftCardCount: 0,
      bonusChecks: 0,     // bonuscheckar, negativt belopp
      bonusCount: 0,      // antal inlösta bonuscheckar
      returnValue: 0,
      returnUnits: 0,
      hasReturn: false,
      noCostGoods:   {revenue:0, revenueEx:0, profit:0},
      noCostService: {revenue:0, revenueEx:0, profit:0}
    };

    // Betalningar. Växelraden har moneyBack=true och negativt belopp,
    // så en rak summering ger det kunden faktiskt betalade.
    for(const p of (r.receiptPayments || [])){
      const amt = Number(p.amount) || 0;
      rec.paid += amt;
      if(!p.moneyBack){
        rec.payments.push({type: p.receiptPaymentType || 'OKÄND', amount: amt});
      }
    }

    const items = r.receiptItems || [];
    const promo = promotionsOf(items);

    for(const it of items){
      const allocation = promo.isAllocation(it);
      if(it.hidden && !allocation) continue;

      const type = it.receiptItemType;

      if(type === 'PROMOTION'){
        // Fördelad på artiklarna: räknas via de dolda raderna nedan, inte här
        if(promo.allocated.has(it.rowIndex)) continue;
        // Ingen fördelning: rabatten bokas på en egen rad utan artikel, så
        // att omsättningen ändå blir det kunden betalade
        const incVat = Number(it.retailTotalIncludingVat) || 0;
        rec.lines.push({
          key: 'kampanj', name: 'Kampanjrabatt', articleNumber: '',
          supplier: '', supplierName: '', group: '', group1: '',
          isService: false, noCost: false, promo: true,
          promoName: promoName(it), promoRow: it.rowIndex,
          unallocated: true,   // hör till kvittot, inte till någon artikel
          q: 0, incVat,
          exVat:  Number(it.retailTotalExcludingVat) || 0,
          profit: Number(it.profitExcludingVat) || 0,
          discount: -incVat
        });
        continue;
      }

      if(type === 'BONUS_CHECK'){
        rec.bonusChecks += Number(it.retailTotalIncludingVat) || 0;
        rec.bonusCount  += 1;
        continue;
      }
      if(type !== 'ARTICLE') continue;      // TEXT och övrigt saknar belopp

      if(it.articleType === 'GIFT_CERTIFICATE'){
        // Ett utfärdat presentkort är en skuld, inte försäljning, och bokförs med
        // negativ vinst i kvittot. Hålls därför utanför produkt- och marginalanalysen.
        // Inlösta presentkort syns istället som betalsätt.
        rec.giftCardBooked += Number(it.retailTotalIncludingVat) || 0;
        rec.giftCardFace   += Number(it.retailPriceIncludingVat) || 0;
        rec.giftCardCount  += 1;
        continue;
      }

      const q      = Number(it.quantity) || 0;
      const incVat = Number(it.retailTotalIncludingVat) || 0;
      const exVat  = Number(it.retailTotalExcludingVat) || 0;
      const profit = Number(it.profitExcludingVat) || 0;
      const cost   = Number(it.costTotalExcludingVat) || 0;
      const isService = it.articleType === 'SERVICE';

      // En rad som säljs utan registrerat inköpspris redovisas med hela beloppet
      // som vinst. Det är ingen verklig marginal utan ett tomt fält i
      // artikelregistret. Gäller både varor och tjänster: en reparation har
      // reservdelar, ett påfyllt kontantkort har ett inköpspris. Varor och
      // tjänster spåras var för sig så att texterna kan vara precisa.
      const noCost = q > 0 && cost === 0;

      const group = typeof it.articleGroupTree === 'string' && it.articleGroupTree
        ? it.articleGroupTree : '';

      rec.lines.push({
        key: String(it.articleNumber ?? it.description ?? 'okänd'),
        name: String(it.description || it.articleNumber || 'Okänd artikel'),
        articleNumber: it.articleNumber != null ? String(it.articleNumber) : '',
        supplier: it.supplierNumber != null && it.supplierNumber !== '' ? String(it.supplierNumber) : '',
        supplierName: typeof it.supplierName === 'string' ? it.supplierName.trim() : '',
        group,
        group1: group ? group.split('||')[0] : '',
        isService, noCost,
        promo: allocation,      // en kampanjrabatt fördelad på artikeln, antal 0
        ...(allocation ? {promoName: promo.nameOf(it.rowIndex), promoRow: it.rowIndex} : {}),
        q, incVat, exVat, profit,
        discount: Number(it.discountAmountIncludingVat) || 0
      });
    }

    rec.hasCustomer = rec.customer !== '';
    sumLines(rec);
    out.push(rec);
  }

  if(!out.length){
    throw new AppError('Inga kvitton med datum',
      'Inget kvitto i filen har datum och tid. Gör en ny kvittoexport från Back office.');
  }

  out.sort((a,b) => a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0);

  // Samma etikett på varje rad för samma leverantör, räknad en gång
  const names = supplierNames(out);
  for(const r of out) for(const ln of r.lines){
    ln.supplierLabel = ln.unallocated ? UNALLOCATED_LABEL : supplierLabel(ln.supplier, names.get(ln.supplier));
  }
  return out;
}

/* --------------------------------------------------------------------------
   Kampanjer

   En kampanj ("2 för 20") syns i exporten två gånger:
     • en synlig rad med receiptItemType PROMOTION och hela rabatten, −10 kr
     • dolda ARTICLE-rader med samma rowIndex och antal 0, en per artikel
       kampanjen gäller, som fördelar rabatten: −5 kr på var och en

   Rabatten räknas en gång, via de dolda raderna. Då hamnar den på rätt
   artikel, och därmed rätt artikelgrupp och leverantör. Går fördelningen
   inte ihop på öret med kampanjraden räknas kampanjraden i stället, som en
   egen rad utan artikel. Andra dolda rader hålls utanför som förut.
   -------------------------------------------------------------------------- */

/** Vilka dolda rader som är kampanjfördelningar, och vilka kampanjer som är fördelade. */
export function promotionsOf(items){
  const promoTotal = new Map();
  const names = new Map();
  for(const it of items){
    if(it.receiptItemType === 'PROMOTION' && !it.hidden){
      promoTotal.set(it.rowIndex, (promoTotal.get(it.rowIndex) || 0) + (Number(it.retailTotalIncludingVat) || 0));
      if(!names.has(it.rowIndex)) names.set(it.rowIndex, promoName(it));
    }
  }
  const isAllocation = it => !!it.hidden && it.receiptItemType === 'ARTICLE' &&
    (Number(it.quantity) || 0) === 0 && promoTotal.has(it.rowIndex);

  const allocSum = new Map();
  for(const it of items){
    if(isAllocation(it)){
      allocSum.set(it.rowIndex, (allocSum.get(it.rowIndex) || 0) + (Number(it.retailTotalIncludingVat) || 0));
    }
  }
  const allocated = new Set([...promoTotal]
    .filter(([k, total]) => allocSum.has(k) && Math.abs(allocSum.get(k) - total) < 0.005)
    .map(([k]) => k));

  return {allocated, isAllocation: it => isAllocation(it) && allocated.has(it.rowIndex),
          nameOf: row => names.get(row) || PROMO_UNNAMED};
}

/** Kampanjens namn som det står på kampanjraden. */
export const PROMO_UNNAMED = 'Kampanj utan namn';
function promoName(it){
  const d = typeof it.description === 'string' ? it.description.replace(/\s+/g, ' ').trim() : '';
  return d || PROMO_UNNAMED;
}

/**
 * Räknar fram kvittots belopp ur dess säljbara rader. Anropas vid inläsning
 * och när urvalet av artikelgrupp eller leverantör snävar in raderna, så att
 * båda vägarna räknar på exakt samma sätt.
 *
 * Skriver nya objekt för noCostGoods och noCostService i stället för att
 * nollställa de befintliga. Ett insnävat kvitto är en kopia av originalet och
 * får aldrig ändra originalets siffror.
 */
export function sumLines(rec){
  rec.revenue = 0; rec.revenueEx = 0; rec.profit = 0; rec.units = 0;
  rec.returnValue = 0; rec.returnUnits = 0; rec.hasReturn = false;
  rec.noCostGoods   = {revenue:0, revenueEx:0, profit:0};
  rec.noCostService = {revenue:0, revenueEx:0, profit:0};

  for(const ln of rec.lines){
    if(ln.noCost){
      const t = ln.isService ? rec.noCostService : rec.noCostGoods;
      t.revenue   += ln.incVat;
      t.revenueEx += ln.exVat;
      t.profit    += ln.profit;
    }
    rec.revenue   += ln.incVat;
    rec.revenueEx += ln.exVat;
    rec.profit    += ln.profit;
    rec.units     += ln.q;

    if(ln.q < 0){
      rec.hasReturn = true;
      rec.returnValue += ln.incVat;
      rec.returnUnits += ln.q;
    }
  }
}
